import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, RotateCcw, Download } from "lucide-react";
import AudioVisualizer from "./AudioVisualizer";
import { cn } from "@/lib/utils";

const AudioPlayer = ({ 
  audioBase64, 
  title = "Subliminal Audio",
  frequency = 432,
  loopEnabled = true,
  onDownload
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.loop = loopEnabled;
    }
  }, [volume, loopEnabled]);

  useEffect(() => {
    if (audioRef.current && audioBase64) {
      audioRef.current.src = `data:audio/mp3;base64,${audioBase64}`;
    }
  }, [audioBase64]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    if (audioBase64) {
      const link = document.createElement('a');
      link.href = `data:audio/mp3;base64,${audioBase64}`;
      link.download = `${title.replace(/\s+/g, '_')}_${frequency}Hz.mp3`;
      link.click();
    }
    onDownload?.();
  };

  if (!audioBase64) return null;

  return (
    <div className="glass-card p-4 space-y-4">
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => !loopEnabled && setIsPlaying(false)}
      />

      {/* Title and Frequency */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-white">{title}</h4>
          <p className="text-sm text-gray-400 font-mono">{frequency} Hz</p>
        </div>
        <AudioVisualizer isPlaying={isPlaying} />
      </div>

      {/* Progress Bar */}
      <div 
        className="h-2 bg-white/10 rounded-full cursor-pointer group"
        onClick={handleSeek}
        data-testid="audio-progress"
      >
        <motion.div
          className="h-full bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full relative"
          style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
        </motion.div>
      </div>

      {/* Time Display */}
      <div className="flex justify-between text-xs text-gray-500 font-mono">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleRestart}
            data-testid="audio-restart"
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <RotateCcw size={18} />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={togglePlay}
            data-testid="audio-play"
            className="w-12 h-12 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-[0_0_20px_rgba(124,58,237,0.4)]"
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-1" />}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleMute}
            data-testid="audio-mute"
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </motion.button>
        </div>

        {/* Volume Slider */}
        <div className="flex items-center gap-2 w-24">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            data-testid="volume-slider"
            className="w-full"
          />
        </div>

        {/* Download */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleDownload}
          data-testid="audio-download"
          className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
        >
          <Download size={18} />
        </motion.button>
      </div>

      {/* Loop Indicator */}
      {loopEnabled && (
        <div className="flex items-center gap-2 text-xs text-violet-400">
          <div className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
          <span>Looping enabled</span>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
